import { defineCopy } from '../pageCopy';
import { getProduct } from '../../products';

const product = getProduct('dilaerp')!;

const en = {
  seo: { title: product.seo.title, description: product.seo.description },
  hero: {
    title: 'the erp that speaks kurdish',
    lede: 'Sales, stock, accounting, HR, payroll and POS in one simple system. Fifteen modules, three languages, your own server.',
    secondary: 'Open live demo',
    liveLabel: product.liveLabel,
    imageAlt: product.imageAlt,
    chipPnl: 'Profit & Loss',
    chipPayroll: 'Payroll',
    chipStock: 'Stock',
  },
  marquee: [
    'Self-hosted',
    'Your data stays yours',
    'Sorani PDFs',
    '71 print formats',
    '26+ reports',
    'AI assistant',
    'Simple mode',
    'Multi-company',
    'کوردی',
    'العربية',
    'English',
  ],
  modules: {
    aria: 'Modules',
    title: 'fifteen modules. one login.',
    lede: 'Everything an ERP needs, curated down to one clean screen per job. No dense legacy tables, no module marketplace roulette.',
    listAria: 'Module list',
    items: [
      'Sales',
      'Purchases',
      'Inventory',
      'Accounting',
      'HR & Payroll',
      'Point of Sale',
      'Projects',
      'Support',
      'CRM',
      'Assets',
      'Reports',
      'Healthcare',
      'LMS',
      'Helpdesk',
      'Stock analytics',
    ],
  },
  ai: {
    aria: 'AI assistant',
    title: 'ask it in kurdish',
    lede: '“داهاتمان لەم مانگەدا چەندە؟” (our revenue this month), answered from live data, in the language you asked in. About fifty-four ERP tools sit behind the chat.',
    rows: [
      {
        title: 'Writes pass an approval card',
        body: 'Create the invoice, record the payment, but only after you confirm the card. Nothing writes itself.',
      },
      {
        title: 'Your permissions, not a backdoor',
        body: 'The assistant runs on your session, so a cashier can never ask it for payroll.',
      },
    ],
    shotAlt:
      'DilaERP AI assistant answering a revenue question written in Kurdish, with the right-to-left sidebar behind.',
  },
  simple: {
    aria: 'Simple mode and POS',
    shotAlt:
      'DilaERP Simple Mode sell screen on mobile with product grid, cart and large checkout button.',
    title: 'head office power, shop-floor simple',
    lede: 'Simple Mode distills the whole ERP to Sell, Stock, Money and More on any phone. Barcode POS, 80mm thermal receipts, shift sessions, tolerant of bad internet.',
    rows: [
      {
        title: 'A cashier learns it in an hour',
        body: 'Big buttons, barcode search, tender modal with your payment modes. No ERP training manual required.',
      },
      {
        title: 'Same books underneath',
        body: 'Every sale posts to the same ledger the accountant sees. Simple on top, serious below.',
      },
    ],
  },
  finance: {
    aria: 'Finance',
    title: 'finance without fear',
    lede: 'Profit and loss, balance sheet, cash flow, trial balance and general ledger, plus twenty-six ready reports. Holding companies roll up into consolidated statements.',
    shotAlt: 'DilaERP profit and loss report with income, cost of goods sold and expenses in IQD.',
  },
  kurdish: {
    aria: 'Kurdish first',
    eyebrow: 'Kurdish first',
    heading: 'یەک سیستەم بۆ تەواوی بزنسەکەت',
    body: 'One system for your whole business. Interface, AI, PDFs and receipts in Sorani, Arabic and English, with right-to-left done properly, down to the digits on an invoice.',
    shotAlt:
      'DilaERP dashboard fully in Kurdish Sorani with a right-to-left layout and localized KPI cards.',
  },
  stats: {
    aria: 'DilaERP in numbers',
    labels: [
      'Business modules',
      'Screens',
      'Strings per language',
      'Built-in reports',
      'AI tools',
      'Print formats',
    ],
  },
  ownership: {
    aria: 'Ownership',
    title: 'self-hosted. your data stays yours.',
    lede: 'Private-server deployments with masked APIs and zero upstream branding. Seventy-one branded print formats across six themes carry your letterhead, with Sorani glyphs embedded so Kurdish PDFs never show boxes.',
    rows: [
      {
        title: 'Your server, your rules',
        body: 'Run it on our cloud or your own machine. Either way the database is yours and exports are one click, always.',
      },
      {
        title: 'Per-business toggles',
        body: 'Modules, themes and features switch per tenant, so a pharmacy and a contractor never see the same clutter.',
      },
      {
        title: 'Proven engine underneath',
        body: 'The business logic rides on the battle-tested ERPNext engine, unmodified. Simple on top, standard below.',
      },
    ],
  },
  gallery: {
    aria: 'More screens',
    title: 'a tour through the system',
    shots: [
      {
        alt: 'DilaERP sales invoice list with IQD amounts and payment statuses.',
        caption: 'Sales invoices',
      },
      { alt: 'DilaERP CRM deals pipeline with open opportunities.', caption: 'CRM deals' },
      {
        alt: 'DilaERP employee list with departments and designations.',
        caption: 'HR and payroll',
      },
      { alt: 'DilaERP chart of accounts tree.', caption: 'Chart of accounts' },
    ],
  },
  demo: {
    title: 'run the whole business in your language',
    sub: 'Books, stock, payroll and POS in Sorani, Arabic or English. We migrate your data and train your team, in Erbil or remote.',
    secondary: 'Open live demo',
    liveNote: 'Live demo',
  },
};

type Copy = typeof en;

const ar: Copy = {
  seo: {
    title: 'DilaERP: نظام ERP اللي يحچي كوردي',
    description:
      'DilaERP من Datacode نظام ERP كامل وبسيط بالكوردي والعربي والإنكليزي: مبيعات ومخزون ومحاسبة وموارد بشرية ورواتب ونقاط بيع وCRM وأكثر من 26 تقرير، مع مساعد ذكاء اصطناعي وملفات PDF بهويتك. استضافة ذاتية وبياناتك تبقى ملكك.',
  },
  hero: {
    title: 'نظام ERP يحچي كوردي',
    lede: 'المبيعات والمخزون والمحاسبة والموارد البشرية والرواتب ونقاط البيع بنظام واحد بسيط. خمسة عشر وحدة، وثلاث لغات، وسيرفرك الخاص.',
    secondary: 'افتح النسخة التجريبية المباشرة',
    liveLabel: 'نسخة تجريبية مباشرة',
    imageAlt:
      'لوحة DilaERP تعرض إيراد الشهر والمبالغ المستحقة والفواتير المتأخرة ومخطط إيراد آخر 30 يوم بالدينار العراقي.',
    chipPnl: 'الأرباح والخسائر',
    chipPayroll: 'الرواتب',
    chipStock: 'المخزون',
  },
  marquee: [
    'استضافة ذاتية',
    'بياناتك تبقى ملكك',
    'ملفات PDF بالسوراني',
    '71 صيغة طباعة',
    '26+ تقرير',
    'مساعد AI',
    'الوضع البسيط',
    'متعدد الشركات',
    'کوردی',
    'العربية',
    'English',
  ],
  modules: {
    aria: 'الوحدات',
    title: 'خمسة عشر وحدة. تسجيل دخول واحد.',
    lede: 'كل شي يحتاجه نظام ERP، مختصر بشاشة وحدة نظيفة لكل مهمة. بدون جداول قديمة مزدحمة، وبدون لعبة الحظ بسوق الإضافات.',
    listAria: 'قائمة الوحدات',
    items: [
      'المبيعات',
      'المشتريات',
      'المخزون',
      'المحاسبة',
      'الموارد البشرية والرواتب',
      'نقاط البيع',
      'المشاريع',
      'الدعم',
      'CRM',
      'الأصول',
      'التقارير',
      'الرعاية الصحية',
      'LMS',
      'مكتب المساعدة',
      'تحليلات المخزون',
    ],
  },
  ai: {
    aria: 'مساعد الذكاء الاصطناعي',
    title: 'اسأله بالكوردي',
    lede: '«داهاتمان لەم مانگەدا چەندە؟» (شكد إيراداتنا هذا الشهر)، والجواب من بيانات حية، وبنفس اللغة اللي سألت بيها. حوالي أربعة وخمسين أداة ERP ورا الدردشة.',
    rows: [
      {
        title: 'أي كتابة تمر ببطاقة موافقة',
        body: 'أنشئ الفاتورة، سجّل الدفعة، بس بعد ما تأكد البطاقة. ماكو شي ينكتب لحاله.',
      },
      {
        title: 'صلاحياتك انت، مو باب خلفي',
        body: 'المساعد يشتغل بجلستك انت، فالكاشير ما يكدر يسأله عن الرواتب أبداً.',
      },
    ],
    shotAlt:
      'مساعد الذكاء الاصطناعي بـ DilaERP يجاوب على سؤال عن الإيراد مكتوب بالكوردية، والشريط الجانبي من اليمين لليسار بالخلفية.',
  },
  simple: {
    aria: 'الوضع البسيط ونقطة البيع',
    shotAlt:
      'شاشة البيع بالوضع البسيط بـ DilaERP على الموبايل، فيها شبكة المنتجات والسلة وزر دفع كبير.',
    title: 'قوة المكتب الرئيسي، وبساطة أرض المحل',
    lede: 'الوضع البسيط يختصر النظام كله بأربع خانات: بيع، مخزون، فلوس، ومزيد، على أي موبايل. نقطة بيع بالباركود، ووصولات حرارية 80 ملم، وجلسات مناوبة، وتتحمل ضعف الإنترنت.',
    rows: [
      {
        title: 'الكاشير يتعلمه بساعة',
        body: 'أزرار كبيرة، وبحث بالباركود، ونافذة دفع بطرق الدفع اللي تعتمدها. ماكو حاجة لدليل تدريب على ERP.',
      },
      {
        title: 'نفس الدفاتر من تحت',
        body: 'كل عملية بيع تنسجل بنفس دفتر الأستاذ اللي يشوفه المحاسب. بسيط من فوق، وجدي من تحت.',
      },
    ],
  },
  finance: {
    aria: 'المالية',
    title: 'المالية بلا خوف',
    lede: 'الأرباح والخسائر، والميزانية العمومية، والتدفق النقدي، وميزان المراجعة، ودفتر الأستاذ العام، بالإضافة لست وعشرين تقرير جاهز. الشركات القابضة تتجمع بقوائم مالية موحدة.',
    shotAlt:
      'تقرير الأرباح والخسائر بـ DilaERP يعرض الإيرادات وتكلفة البضاعة المباعة والمصاريف بالدينار العراقي.',
  },
  kurdish: {
    aria: 'الكوردية أولاً',
    eyebrow: 'الكوردية أولاً',
    heading: 'یەک سیستەم بۆ تەواوی بزنسەکەت',
    body: 'نظام واحد لكل شغلك. الواجهة والذكاء الاصطناعي وملفات PDF والوصولات بالسوراني والعربي والإنكليزي، مع اتجاه اليمين لليسار مضبوط صح، لين الأرقام على الفاتورة.',
    shotAlt:
      'لوحة DilaERP كلها بالكوردية السورانية، بتخطيط من اليمين لليسار وبطاقات مؤشرات مترجمة.',
  },
  stats: {
    aria: 'DilaERP بالأرقام',
    labels: [
      'وحدات أعمال',
      'شاشة',
      'نص واجهة لكل لغة',
      'تقرير مدمج',
      'أدوات AI',
      'صيغة طباعة',
    ],
  },
  ownership: {
    aria: 'الملكية',
    title: 'استضافة ذاتية. بياناتك تبقى ملكك.',
    lede: 'تنصيب على سيرفرات خاصة مع واجهات API مخفية وبدون أي علامة تجارية من المصدر. واحد وسبعين صيغة طباعة بهويتك، موزعة على ست ثيمات، تحمل ترويستك، وخطوط السوراني مضمّنة داخلها حتى ملفات PDF الكوردية ما تظهر فيها مربعات.',
    rows: [
      {
        title: 'سيرفرك، وقوانينك',
        body: 'شغّله على السحابة مالتنا أو على جهازك. بالحالتين قاعدة البيانات إلك والتصدير بضغطة وحدة دائماً.',
      },
      {
        title: 'خيارات تشغيل لكل شركة',
        body: 'الوحدات والثيمات والميزات تنفعّل وتنطفي لكل عميل، فالصيدلية والمقاول ما يشوفون نفس الزحمة.',
      },
      {
        title: 'محرك مجرَّب من تحت',
        body: 'منطق العمل يشتغل على محرك ERPNext المجرَّب ميدانياً، بدون تعديل. بسيط من فوق، وقياسي من تحت.',
      },
    ],
  },
  gallery: {
    aria: 'شاشات أكثر',
    title: 'جولة داخل النظام',
    shots: [
      {
        alt: 'قائمة فواتير المبيعات بـ DilaERP مع المبالغ بالدينار العراقي وحالات الدفع.',
        caption: 'فواتير المبيعات',
      },
      { alt: 'مسار صفقات CRM بـ DilaERP مع الفرص المفتوحة.', caption: 'صفقات CRM' },
      {
        alt: 'قائمة الموظفين بـ DilaERP مع الأقسام والمسميات الوظيفية.',
        caption: 'الموارد البشرية والرواتب',
      },
      { alt: 'شجرة دليل الحسابات بـ DilaERP.', caption: 'دليل الحسابات' },
    ],
  },
  demo: {
    title: 'شغّل شركتك كلها بلغتك',
    sub: 'الدفاتر والمخزون والرواتب ونقاط البيع بالسوراني أو العربي أو الإنكليزي. ننقل بياناتك وندرّب فريقك، بأربيل أو عن بُعد.',
    secondary: 'افتح النسخة التجريبية المباشرة',
    liveNote: 'نسخة تجريبية مباشرة',
  },
};

const ckb: Copy = {
  seo: {
    title: 'DilaERP: سیستەمی ERP کە بە کوردی قسە دەکات',
    description:
      'DilaERP لەلایەن Datacode سیستەمێکی تەواو و سادەی ERP یە بە کوردی، عەرەبی و ئینگلیزی: فرۆشتن، کۆگا، ژمێریاری، سەرچاوە مرۆییەکان و مووچە، خاڵی فرۆشتن، CRM و زیاتر لە 26 ڕاپۆرت، لەگەڵ یاریدەدەری زیرەکی دەستکرد و PDFی براندکراو. لەسەر سێرڤەری خۆت، و داتاکانت هی خۆتن.',
  },
  hero: {
    title: 'سیستەمی ERP بە کوردی قسە دەکات',
    lede: 'فرۆشتن، کۆگا، ژمێریاری، سەرچاوە مرۆییەکان، مووچە و خاڵی فرۆشتن لە یەک سیستەمی سادەدا. پازدە مۆدیوڵ، سێ زمان و سێرڤەری تایبەت بە خۆت.',
    secondary: 'دیمۆی زیندوو بکەرەوە',
    liveLabel: 'دیمۆی زیندوو',
    imageAlt:
      'داشبۆردی DilaERP کە داهاتی مانگانە، پارەی وەرنەگیراو، فاکتووری دواکەوتوو و هێڵکاری داهاتی 30 ڕۆژ بە دینار نیشان دەدات.',
    chipPnl: 'قازانج و زیان',
    chipPayroll: 'مووچە',
    chipStock: 'کۆگا',
  },
  marquee: [
    'لەسەر سێرڤەری خۆت',
    'داتاکانت هی خۆتن',
    'PDF بە سۆرانی',
    '71 فۆرماتی چاپکردن',
    '26+ ڕاپۆرت',
    'یاریدەدەری AI',
    'دۆخی سادە',
    'فرە کۆمپانیا',
    'کوردی',
    'العربية',
    'English',
  ],
  modules: {
    aria: 'مۆدیوڵەکان',
    title: 'پازدە مۆدیوڵ. یەک چوونەژوورەوە.',
    lede: 'هەموو ئەو شتانەی سیستەمێکی ERP پێویستی پێیەتی، کورت کراوەتەوە بۆ یەک شاشەی پاک بۆ هەر کارێک. بێ خشتەی چڕی کۆن، بێ قومارکردن لە بازاڕی مۆدیوڵ.',
    listAria: 'لیستی مۆدیوڵەکان',
    items: [
      'فرۆشتن',
      'کڕین',
      'کۆگا',
      'ژمێریاری',
      'سەرچاوە مرۆییەکان و مووچە',
      'خاڵی فرۆشتن',
      'پڕۆژەکان',
      'پشتگیری',
      'CRM',
      'موڵک و دارایی',
      'ڕاپۆرتەکان',
      'تەندروستی',
      'LMS',
      'مێزی یارمەتی',
      'شیکاری کۆگا',
    ],
  },
  ai: {
    aria: 'یاریدەدەری زیرەکی دەستکرد',
    title: 'بە کوردی لێی بپرسە',
    lede: '«داهاتمان لەم مانگەدا چەندە؟» وەڵامەکەی لە داتای زیندووەوە دێت، بەو زمانەی پرسیارت پێ کرد. نزیکەی پەنجا و چوار ئامرازی ERP لە پشت چاتەکەوەن.',
    rows: [
      {
        title: 'هەموو نووسینێک بە کارتی پەسەندکردندا دەڕوات',
        body: 'فاکتوورەکە دروست بکە، پارەدانەکە تۆمار بکە، بەڵام تەنها دوای ئەوەی کارتەکە پشتڕاست بکەیتەوە. هیچ شتێک خۆی نانووسرێت.',
      },
      {
        title: 'دەسەڵاتەکانی تۆ، نەک دەرگایەکی پشتەوە',
        body: 'یاریدەدەرەکە لەسەر دانیشتنی تۆ کار دەکات، بۆیە کاشێر هەرگیز ناتوانێت مووچەی لێ بپرسێت.',
      },
    ],
    shotAlt:
      'یاریدەدەری زیرەکی دەستکردی DilaERP کە وەڵامی پرسیارێکی داهات دەداتەوە کە بە کوردی نووسراوە، لەگەڵ شریتی لای ڕاست بۆ چەپ لە پشتەوە.',
  },
  simple: {
    aria: 'دۆخی سادە و خاڵی فرۆشتن',
    shotAlt:
      'شاشەی فرۆشتنی دۆخی سادەی DilaERP لەسەر مۆبایل لەگەڵ تۆڕی بەرهەم، سەبەتە و دوگمەی گەورەی پارەدان.',
    title: 'هێزی بارەگای سەرەکی، سادەیی ناو دووکان',
    lede: 'دۆخی سادە هەموو سیستەمەکە کورت دەکاتەوە بۆ فرۆشتن، کۆگا، پارە و زیاتر لەسەر هەر مۆبایلێک. خاڵی فرۆشتنی بارکۆد، پسوولەی گەرمی 80 ملم، دانیشتنی شێفت، و بەرگەی ئینتەرنێتی خراپ دەگرێت.',
    rows: [
      {
        title: 'کاشێرێک لە یەک کاتژمێردا فێری دەبێت',
        body: 'دوگمەی گەورە، گەڕان بە بارکۆد، پەنجەرەی پارەدان بە شێوازەکانی پارەدانی تۆ. پێویستت بە پەرتووکی ڕاهێنانی ERP نییە.',
      },
      {
        title: 'هەمان دەفتەر لە ژێرەوە',
        body: 'هەر فرۆشتنێک دەچێتە هەمان دەفتەری گشتی کە ژمێریار دەیبینێت. سادە لە سەرەوە، جددی لە خوارەوە.',
      },
    ],
  },
  finance: {
    aria: 'دارایی',
    title: 'دارایی بێ ترس',
    lede: 'قازانج و زیان، تەرازووی گشتی، ڕەوتی پارەی نەختینە، تەرازووی تاقیکردنەوە و دەفتەری گشتی، لەگەڵ بیست و شەش ڕاپۆرتی ئامادە. کۆمپانیا باوکەکان لە ڕاپۆرتی یەکخراودا کۆ دەبنەوە.',
    shotAlt:
      'ڕاپۆرتی قازانج و زیانی DilaERP لەگەڵ داهات، تێچووی کاڵای فرۆشراو و خەرجییەکان بە دینار.',
  },
  kurdish: {
    aria: 'سەرەتا کوردی',
    eyebrow: 'سەرەتا کوردی',
    heading: 'یەک سیستەم بۆ تەواوی بزنسەکەت',
    body: 'ڕووکار، زیرەکی دەستکرد، PDF و پسوولەکان بە سۆرانی، عەرەبی و ئینگلیزی، لەگەڵ ڕاست بۆ چەپی ڕێکوپێک، تەنانەت ژمارەکانی ناو فاکتوورەکەش.',
    shotAlt:
      'داشبۆردی DilaERP بە تەواوی بە کوردی سۆرانی، لەگەڵ ڕووکاری ڕاست بۆ چەپ و کارتی پێوەری وەرگێڕدراو.',
  },
  stats: {
    aria: 'DilaERP بە ژمارە',
    labels: [
      'مۆدیوڵی بزنس',
      'شاشە',
      'دەق بۆ هەر زمانێک',
      'ڕاپۆرتی ناوخۆیی',
      'ئامرازی AI',
      'فۆرماتی چاپکردن',
    ],
  },
  ownership: {
    aria: 'خاوەندارێتی',
    title: 'لەسەر سێرڤەری خۆت. داتاکانت هی خۆتن.',
    lede: 'دامەزراندن لەسەر سێرڤەری تایبەت، لەگەڵ API ی شاراوە و بێ هیچ براندێکی سەرچاوە. حەفتا و یەک فۆرماتی چاپکردنی براندکراو لە شەش ستایلدا سەرپەڕەی نامەکەی تۆ هەڵدەگرن، و پیتی سۆرانی تێیاندا چەسپاوە بۆ ئەوەی PDFی کوردی هەرگیز چوارگۆشە نیشان نەدات.',
    rows: [
      {
        title: 'سێرڤەری تۆ، یاساکانی تۆ',
        body: 'لەسەر کڵاودی ئێمە یان ئامێری خۆت کاری پێ بکە. لە هەردوو حاڵەتدا بنکەی داتاکە هی تۆیە و هەناردەکردن هەمیشە بە یەک کرتەیە.',
      },
      {
        title: 'گۆڕین بۆ هەر بزنسێک',
        body: 'مۆدیوڵ، ستایل و تایبەتمەندییەکان بۆ هەر کڕیارێک دادەگیرسێن و دادەمرکێن، بۆیە دەرمانخانەیەک و پیمانکارێک هەرگیز هەمان قەرەباڵغی نابینن.',
      },
      {
        title: 'مەکینەیەکی تاقیکراوە لە ژێرەوە',
        body: 'لۆژیکی بزنسەکە لەسەر مەکینەی ERPNext کار دەکات کە لە مەیدان تاقیکراوەتەوە، بێ دەستکاری. سادە لە سەرەوە، ستانداردی لە خوارەوە.',
      },
    ],
  },
  gallery: {
    aria: 'شاشەی زیاتر',
    title: 'گەشتێک بە ناو سیستەمەکەدا',
    shots: [
      {
        alt: 'لیستی فاکتووری فرۆشتنی DilaERP لەگەڵ بڕە پارەکان بە دینار و دۆخی پارەدان.',
        caption: 'فاکتووری فرۆشتن',
      },
      {
        alt: 'پایپلاینی مامەڵەکانی CRM لە DilaERP لەگەڵ هەلە فرۆشتنە کراوەکان.',
        caption: 'مامەڵەکانی CRM',
      },
      {
        alt: 'لیستی کارمەندانی DilaERP لەگەڵ بەشەکان و پلەی کار.',
        caption: 'سەرچاوە مرۆییەکان و مووچە',
      },
      { alt: 'داری هەژمارەکانی DilaERP.', caption: 'خشتەی هەژمارەکان' },
    ],
  },
  demo: {
    title: 'هەموو بزنسەکەت بە زمانی خۆت بەڕێوە ببە',
    sub: 'دەفتەر، کۆگا، مووچە و خاڵی فرۆشتن بە سۆرانی، عەرەبی یان ئینگلیزی. داتاکانت دەگوازینەوە و تیمەکەت ڕا دەهێنین، لە هەولێر یان لە دوورەوە.',
    secondary: 'دیمۆی زیندوو بکەرەوە',
    liveNote: 'دیمۆی زیندوو',
  },
};

export const dilaErpCopy = defineCopy(en, ar, ckb);
